import { RESPONSE_PROMISE_SV } from "@/content/response-times";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { persistLead, captureBlockedLead } from "@/lib/lead-fallback.server";
import { confirmationEmail, internalLeadEmail, LEAD_INBOX, LEAD_NOTIFY, sendEmail } from "@/lib/email/resend.server";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit.server";

const Schema = z.object({
  service_slug: z.string().min(1).max(80).regex(/^[a-z0-9-]+$/),
  email: z.string().email().max(255),
  company: z.string().max(120).optional().nullable(),
  brief: z.string().max(2000).optional().nullable(),
  source_path: z.string().max(255).optional().nullable(),
  user_agent: z.string().max(500).optional().nullable(),
  session_id: z.string().uuid().nullable().optional(),
  visitor_id: z.string().uuid().nullable().optional(),
});

export const requestPrimeQuote = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => Schema.parse(input))
  .handler(async ({ data }) => {
    const ip = getClientIp(getRequest());
    if (!checkRateLimit({ scope: "prime-quote", keys: [ip, data.email], limit: 5, windowMs: 60 * 60 * 1000 })) {
      await captureBlockedLead({
        flow: "prime-quote",
        reason: "rate-limit",
        email: data.email,
        company: data.company ?? null,
        message: data.brief ?? null,
        source_path: data.source_path ?? null,
        user_agent: data.user_agent ?? null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
        extra: { service_slug: data.service_slug },
      });
      return { ok: false as const };
    }
    try {
      await persistLead("lead_magnet_requests", {
          magnet_slug: `prime-quote-${data.service_slug}`,
          email: data.email,
          company: data.company ?? null,
          source_path: data.source_path ?? null,
          user_agent: data.user_agent ?? null,
          funnel_id: data.brief ? data.brief.slice(0, 240) : null,
          session_id: data.session_id ?? null,
          visitor_id: data.visitor_id ?? null,
        }, {
          source: "prime-quote",
          email: data.email,
          company: data.company ?? null,
          source_path: data.source_path ?? null,
          session_id: data.session_id ?? null,
          visitor_id: data.visitor_id ?? null,
        });
    } catch (e) {
      console.error("[prime-quote] unexpected:", e);
    }
    const internal = internalLeadEmail({
      subject: `Prime-offertförfrågan — ${data.service_slug}`,
      heading: "Ny offertförfrågan (Prime)",
      fields: {
        "Tjänst": data.service_slug,
        "E-post": data.email,
        "Företag": data.company,
        "Brief": data.brief,
        "Källa": data.source_path,
      },
    });
    const confirm = confirmationEmail({
      intro: `Tack för din offertförfrågan kring ${data.service_slug}. Vi återkommer med en skräddarsydd offert. ${RESPONSE_PROMISE_SV}`,
    });
    await Promise.allSettled([
      sendEmail({ to: LEAD_NOTIFY, subject: internal.subject, html: internal.html, replyTo: data.email,
        logContext: { kind: "internal_lead", leadSource: "prime", leadEmail: data.email } }),
      sendEmail({ to: data.email, subject: confirm.subject, html: confirm.html, replyTo: LEAD_INBOX,
        logContext: { kind: "lead_confirmation", leadSource: "prime", leadEmail: data.email } }),
    ]);
    return { ok: true as const };
  });
